// src/components/dashboard/ConfidenceChart.jsx
import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { TrendingUp } from "lucide-react";
import {
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

/**
 * Mirrors your original ConfidenceChart layout/behavior,
 * adapted to the current CRA setup (no "@/..." aliases, no Tailwind).
 */
export default function ConfidenceChart({ problems = [], isLoading }) {
  // Custom animation for bars
  const animationDuration = 1000;
  const data = useMemo(() => {
    if (!Array.isArray(problems) || problems.length === 0) return [];

    const ranges = [
      { name: "0-20%", min: 0, max: 20, count: 0, color: "#c9ccf9" },
      { name: "21-40%", min: 21, max: 40, count: 0, color: "#a9aef5" },
      { name: "41-60%", min: 41, max: 60, count: 0, color: "#868cee" },
      { name: "61-80%", min: 61, max: 80, count: 0, color: "#6466e8" },
      { name: "81-100%", min: 81, max: 100, count: 0, color: "#4e47e4" },
    ];

    for (const p of problems) {
      // Handle both JSONB confidence_score format and individual score fields
      let d, f, v;

      if (p?.confidence_score && typeof p.confidence_score === 'object') {
        // Database format: JSONB field
        d = Number(p.confidence_score.desirability || 0);
        f = Number(p.confidence_score.feasibility || 0);
        v = Number(p.confidence_score.viability || 0);
      } else {
        // Legacy format: separate fields
        d = Number(p?.desirability_score || 0);
        f = Number(p?.feasibility_score || 0);
        v = Number(p?.viability_score || 0);
      }

      const avg = (d + f + v) / 3;
      const bucket = ranges.find((r) => avg >= r.min && avg <= r.max);
      if (bucket) {
        bucket.count += 1;
      }
    }

    // Only log in development when there's actual data
    if (problems.length > 0 && process.env.NODE_ENV === 'development') {
      console.log('📈 ConfidenceChart final data:', ranges.filter(r => r.count > 0));
    }
    return ranges;
  }, [problems]);

  const insights = useMemo(() => {
    const totalProblems = problems.length;
    const highConfidence = data.filter(d => d.name.includes('61-80%') || d.name.includes('81-100%')).reduce((sum, d) => sum + d.count, 0);
    const lowConfidence = data.filter(d => d.name.includes('0-20%') || d.name.includes('21-40%')).reduce((sum, d) => sum + d.count, 0);
    const avgConfidence = totalProblems > 0 ? Math.round(
      problems.reduce((sum, p) => {
        let d, f, v;
        if (p?.confidence_score && typeof p.confidence_score === 'object') {
          d = Number(p.confidence_score.desirability || 0);
          f = Number(p.confidence_score.feasibility || 0);
          v = Number(p.confidence_score.viability || 0);
        } else {
          d = Number(p.desirability_score || 0);
          f = Number(p.feasibility_score || 0);
          v = Number(p.viability_score || 0);
        }
        return sum + (d + f + v) / 3;
      }, 0) / totalProblems
    ) : 0;

    return {
      totalProblems,
      highConfidence,
      lowConfidence,
      avgConfidence,
      highConfidencePercent: totalProblems > 0 ? Math.round((highConfidence / totalProblems) * 100) : 0
    };
  }, [problems, data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--fg)" }}>
          <TrendingUp size={20} color="var(--accent)" />
          Problem Confidence Distribution
        </CardTitle>
        <p style={{ margin: 0, fontSize: 13, color: "var(--fg-muted)" }}>
          Average confidence across Desirability, Feasibility, and Viability
        </p>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Skeleton style={{ height: 256, width: "100%", borderRadius: 12 }} />
        ) : problems.length > 0 ? (
          <div style={{ width: "100%", height: "auto" }}>
            {/* Beautiful CSS Bar Chart */}
            <div style={{
              padding: '24px',
              background: 'var(--bg-surface)',
              borderRadius: '12px',
              border: '1px solid rgba(226,232,240,0.8)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
            }}>
              {/* Chart Area */}
              <div style={{
                display: 'flex',
                alignItems: 'end',
                gap: '12px',
                height: '220px',
                marginBottom: '16px',
                padding: '0 8px',
                position: 'relative'
              }}>
                {/* Y-axis labels */}
                <div style={{
                  position: 'absolute',
                  left: '-8px',
                  top: '0',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  fontSize: '10px',
                  color: 'var(--fg-muted)'
                }}>
                  {(() => {
                    const maxCount = Math.max(...data.map(d => d.count));
                    const steps = Math.max(4, Math.ceil(maxCount / 2));
                    return Array.from({ length: steps + 1 }, (_, i) => (
                      <div key={i} style={{ textAlign: 'right', paddingRight: '4px' }}>
                        {Math.round((maxCount * (steps - i)) / steps)}
                      </div>
                    ));
                  })()}
                </div>

                {/* Grid lines */}
                <div style={{
                  position: 'absolute',
                  left: '0',
                  right: '0',
                  top: '0',
                  height: '100%',
                  pointerEvents: 'none'
                }}>
                  {Array.from({ length: 5 }, (_, i) => (
                    <div key={i} style={{
                      position: 'absolute',
                      left: '0',
                      right: '0',
                      top: `${i * 25}%`,
                      height: '1px',
                      background: 'rgba(226, 232, 240, 0.5)',
                      opacity: i === 4 ? 0.8 : 0.3
                    }} />
                  ))}
                </div>

                {/* Bars */}
                {data.map((item, idx) => {
                  const maxCount = Math.max(...data.map(d => d.count));
                  const height = maxCount > 0 ? (item.count / maxCount) * 200 : 0;
                  return (
                    <div
                      key={idx}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      {/* Value label on top */}
                      <div style={{
                        fontSize: '12px',
                        marginBottom: '6px',
                        color: 'var(--fg-secondary)',
                        fontWeight: '600',
                        opacity: item.count > 0 ? 1 : 0.5,
                        transform: 'translateY(-4px)'
                      }}>
                        {item.count}
                      </div>

                      {/* Bar */}
                      <div
                        style={{
                          width: '100%',
                          maxWidth: '48px',
                          background: item.count > 0 ? `linear-gradient(180deg, ${item.color}, ${item.color}dd)` : 'var(--bg-subtle)',
                          height: `${Math.max(height, item.count > 0 ? 8 : 2)}px`,
                          borderRadius: '6px 6px 2px 2px',
                          boxShadow: item.count > 0 ? `0 4px 12px ${item.color}40` : 'none',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: item.count > 0 ? 'pointer' : 'default',
                          position: 'relative',
                          border: item.count > 0 ? `2px solid ${item.color}20` : '1px solid var(--border)',
                          animation: `slideUp 0.8s ease-out ${idx * 0.1}s both`
                        }}
                        onMouseEnter={(e) => {
                          if (item.count > 0) {
                            e.target.style.transform = 'scaleY(1.05) scaleX(1.1)';
                            e.target.style.boxShadow = `0 8px 20px ${item.color}60`;
                            e.target.style.filter = 'brightness(1.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scaleY(1) scaleX(1)';
                          e.target.style.boxShadow = item.count > 0 ? `0 4px 12px ${item.color}40` : 'none';
                          e.target.style.filter = 'brightness(1)';
                        }}
                        title={`${item.name}: ${item.count} problem${item.count !== 1 ? 's' : ''}`}
                      />

                      {/* Label */}
                      <div style={{
                        fontSize: '10px',
                        marginTop: '8px',
                        color: 'var(--fg-muted)',
                        textAlign: 'center',
                        fontWeight: '500',
                        lineHeight: '1.2'
                      }}>
                        {item.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Insights Summary */}
            <div style={{
              marginTop: 16,
              padding: 20,
              background: "var(--bg-inset)",
              borderRadius: 12,
              border: "1px solid rgba(14, 165, 233, 0.2)",
              boxShadow: "0 4px 12px rgba(14, 165, 233, 0.1)"
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: "var(--accent-fg)",
                    textShadow: "0 1px 2px rgba(30, 64, 175, 0.1)"
                  }}>
                    {insights.avgConfidence}%
                  </div>
                  <div style={{ fontSize: 12, color: "var(--fg-secondary)", fontWeight: 600, marginTop: 2 }}>Average Confidence</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: "var(--success)",
                    textShadow: "0 1px 2px rgba(5, 150, 105, 0.1)"
                  }}>
                    {insights.highConfidence}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--fg-secondary)", fontWeight: 600, marginTop: 2 }}>High Confidence</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: "var(--danger)",
                    textShadow: "0 1px 2px rgba(220, 38, 38, 0.1)"
                  }}>
                    {insights.lowConfidence}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--fg-secondary)", fontWeight: 600, marginTop: 2 }}>Needs Validation</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: "var(--accent-fg)",
                    textShadow: "0 1px 2px rgba(124, 58, 237, 0.1)"
                  }}>
                    {insights.highConfidencePercent}%
                  </div>
                  <div style={{ fontSize: 12, color: "var(--fg-secondary)", fontWeight: 600, marginTop: 2 }}>Ready for Delivery</div>
                </div>
              </div>
            </div>

            {/* CSS Animation Keyframes */}
            <style>{`
              @keyframes slideUp {
                from {
                  height: 0;
                  opacity: 0;
                  transform: translateY(20px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "36px 0" }}>
            <TrendingUp size={48} color="var(--border-strong)" style={{ marginBottom: 8 }} />
            <p style={{ margin: 0, color: "var(--fg-muted)" }}>No problem cards to analyze</p>
            <p style={{ margin: 0, marginTop: 4, fontSize: 12, color: "var(--fg-subtle)" }}>
              Create problem cards to see confidence distribution
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
